/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2018, Zumper
 * @description  a11y handlers for keyboard events
 */
import { Keys } from '@zcode/utils'
import invariant from 'tiny-invariant'

// Create a memoized key handler creator. Used for creating key handlers that
// respond only to specific keys. This function creates a creator :) See below
// for examples of common keyHandlerCreator's you might need.
export const createKeyHandler = (...keyCodes: Keys[]) => {
  const cache = new WeakMap<
    | ((event: React.KeyboardEvent<unknown>) => void)
    | ((event: KeyboardEvent) => void),
    | ((event: React.KeyboardEvent<any>) => void)
    | ((event: KeyboardEvent) => void)
  >()
  const keyCode = keyCodes[0]
  const multipleKeyCodes = Array.isArray(keyCodes) && keyCodes.length > 1
  const hasSpace = multipleKeyCodes
    ? keyCodes.includes(Keys.SPACE)
    : keyCode === Keys.SPACE
  return <
    T extends
      | ((event: React.KeyboardEvent<any>) => void)
      | ((event: KeyboardEvent) => void),
  >(
    handler: T
  ): T => {
    if (!cache.has(handler)) {
      cache.set(handler, (event: Parameters<T>[0]) => {
        const matches = multipleKeyCodes
          ? keyCodes.includes(event.which)
          : keyCode === event.which
        if (matches) {
          if (hasSpace && event.which === Keys.SPACE) {
            event.preventDefault()
          }
          // @ts-expect-error TS isn't sure if we have a react or native handler/event
          handler(event)
        }
      })
    }
    const filteredHandler = cache.get(handler)
    invariant(filteredHandler)
    // @ts-expect-error TS isn't sure if we have a react or native handler
    return filteredHandler
  }
}

// https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_button_role#Keyboard_and_focus
// Quote: Buttons are expected to be triggered using the Space or Enter key,
// while links are expected to be triggered using [only] the Enter key.

// http://www.karlgroves.com/2014/11/24/ridiculously-easy-trick-for-keyboard-accessibility/
// <div onClick={onClick} onKeyPress={a11yButtonHandler(onClick)} role="button" tabIndex="0" />
export const a11yButtonHandler = createKeyHandler(Keys.ENTER, Keys.SPACE)

// <div onClick={onClick} onKeyPress={a11yLinkHandler(onClick)} role="link" tabIndex="0" />
export const a11yLinkHandler = createKeyHandler(Keys.ENTER)

// <div onKeyPress={enterHandler(onEnterPress)} role="button" tabIndex="0" />
export const enterHandler = createKeyHandler(Keys.ENTER)

// <div onKeyPress={spaceHandler(onSpacePress)} role="button" tabIndex="0" />
export const spaceHandler = createKeyHandler(Keys.SPACE)

// <div onKeyPress={escHandler(onEscPress)} role="button" tabIndex="0" />
export const escHandler = createKeyHandler(Keys.ESC)

export const arrowHandler = createKeyHandler(
  Keys.ARROW_DOWN,
  Keys.ARROW_LEFT,
  Keys.ARROW_RIGHT,
  Keys.ARROW_UP
)

export const horizontalArrowHandler = createKeyHandler(
  Keys.ARROW_LEFT,
  Keys.ARROW_RIGHT
)

export const verticalArrowHandler = createKeyHandler(
  Keys.ARROW_DOWN,
  Keys.ARROW_UP
)
